/* eslint-disable react/button-has-type */
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { _ } from '_locales';
import Overview from './pages/Overview';
import AddressSearch from './pages/AddressSearch';
import { DownloadAppBanner } from '../../components';
import SelectBulkItems from './pages/SelectBulkItems';
import PickupDate from './pages/PickupDate';
import ContactInformation from './pages/ContactInformation';
import ConfirmDetails from './pages/ConfirmDetails';
import Confirmation from './pages/Confirmation';
import ExtraSelection from './pages/ExtraSelection';
import AddressSearchMap from './pages/AddressSearchMap';

const OpacityAnimation = keyframes`
  from { opacity: 0;}
  to { opacity:1;}
`;

const View = styled.div`
  animation: ${OpacityAnimation} 0.5s;
`;

const BulkPickup = ({
  bpData, noDownloadBanner, prompts, has_app_download
}) => {
  const returnIdFromParams = new URLSearchParams(
    window.location.hash.slice(1)
  ).get('rc-bulk-payment');

  const { state } = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const [bpItems, setBpItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState();
  const [dropdownSelection, setDropdownSelection] = useState();
  const [windowsWidth, setWindowsWidth] = useState(
    document.getElementById('rcroot').getBoundingClientRect().width
  );

  const [addressSearchType, setAddressSearchType] = useState();

  const hasPayment = bpData?.bulk_pickup_config?.has_payment;
  const paymentSystem = bpData?.bulk_pickup_config?.payment_system;
  const submitFormFields = bpData?.bulk_pickup_config?.contact_page_config || {
    name: 'optional',
    email: 'optional',
    phone: 'optional',
  };

  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  });

  const [addressDetails, setAddressDetails] = useState({
    projectId: '',
    districtId: '',
    zoneString: '',
    address: '',
  });

  const pageNames = [
    { name: 'overview', isActive: true },
    { name: 'address-search', isActive: true },
    { name: 'select-bulk-items', isActive: true },
    { name: 'pickup-date', isActive: true },
    {
      name: 'extra-information',
      isActive: bpData?.bulk_pickup_config?.has_extra_dropdown,
    },
    { name: 'contact-information', isActive: true },
    { name: 'confirm-details', isActive: true },
    { name: 'confirmation', isActive: true },
  ].filter((i) => i.isActive);

  useEffect(() => {
    if (prompts[0]?.zoneSelectOptions?.map && !prompts[0]?.zoneSelectOptions?.search) {
      setAddressSearchType('map');
    } else {
      setAddressSearchType('search');
    }
  }, []);

  useEffect(() => {
    const updateWindowDimensions = () => {
      setWindowsWidth(
        document.getElementById('rcroot').getBoundingClientRect().width
      );
    };
    window.addEventListener('resize', updateWindowDimensions);
    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  useEffect(() => {
    if (bpData?.bulk_pickup_config?.categories.length > 0) {
      const { materials, categories } = bpData.bulk_pickup_config;
      const bpItems = {};
      categories.forEach((i) => {
        bpItems[i.mat_cat_nm.split(' ').join('')] = {
          name: i.mat_cat_nm,
          options: materials
            .filter((mat) => mat.mat_cat_id === i.mat_cat_id)
            .map((it) => ({ ...it, count: 0 })),
        };
      });
      setBpItems(bpItems);
    }

    if (state?.returnId || returnIdFromParams) {
      setCurrentPage(
        pageNames.findIndex((item) => item.name === 'confirmation')
      );
    }
  }, []);

  const pages = [
    {
      isActive: true,
      component: (
        <Overview
          bpData={bpData}
          bpItems={bpItems}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          noDownloadBanner={noDownloadBanner}
          hasPayment={hasPayment}
        />
      ),
    },
    {
      isActive: true,
      component: (
        addressSearchType === 'map' ? (
          <AddressSearchMap
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            setAddressDetails={setAddressDetails}
            setAddressSearchType={setAddressSearchType}
          />
        ) : (
          <AddressSearch
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            setAddressDetails={setAddressDetails}
          />
        )
      ),
    },
    {
      isActive: true,
      component: (
        <SelectBulkItems
          bpData={bpData}
          setBpItems={setBpItems}
          bpItems={bpItems}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          hasPayment={hasPayment}
        />
      ),
    },

    {
      isActive: true,
      component: (
        <PickupDate
          bpData={bpData}
          bpItems={bpItems}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          addressDetails={addressDetails}
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
        />
      ),
    },
    {
      isActive: bpData?.bulk_pickup_config?.has_extra_dropdown,
      component: (
        <ExtraSelection
          bpData={bpData}
          bpItems={bpItems}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setDropdownSelection={setDropdownSelection}
          dropdownSelection={dropdownSelection}
        />
      ),
    },
    {
      isActive: !(submitFormFields.name === 'hidden' && submitFormFields.email === 'hidden' && submitFormFields.phone === 'hidden'),
      component: (
        <ContactInformation
          bpData={bpData}
          bpItems={bpItems}
          windowsWidth={windowsWidth}
          setContactInfo={setContactInfo}
          contactInfo={contactInfo}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          addressDetails={addressDetails}
          submitFormFields={submitFormFields}
        />
      ),
    },
    {
      isActive: true,
      component: (
        <ConfirmDetails
          bpData={bpData}
          bpItems={bpItems}
          pageNames={pageNames}
          setContactInfo={setContactInfo}
          contactInfo={contactInfo}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setBpItems={setBpItems}
          selectedDate={selectedDate}
          addressDetails={addressDetails}
          dropdownSelection={dropdownSelection}
          hasPayment={hasPayment}
          paymentSystem={paymentSystem}
        />
      ),
    },
    {
      isActive: true,
      component: (
        <Confirmation
          bpItems={bpItems}
          addressDetails={addressDetails}
          selectedDate={selectedDate}
          contactInfo={contactInfo}
          returnId={state?.returnId || returnIdFromParams}
          setCurrentPage={setCurrentPage}
        />
      ),
    },
  ].filter((i) => i.isActive);

  if (!bpData || Object.values(bpData).length === 0) {
    return (
      <h2 style={{ textAlign: 'center' }}>
        {_('bulkPickup.something_went_wrong')}
      </h2>
    );
  }
  return (
    <View style={{ padding: 10 }}>
      {pages[currentPage].component}
      {currentPage === 0 && !noDownloadBanner && has_app_download && <DownloadAppBanner />}
    </View>
  );
};

export default connect(
  (state) => ({
    bpData: state.bulkPickup.bpData,
    noDownloadBanner: state?.config?.webappConfig?.feature === 'bulk_pickup',
    prompts: state.zone?.prompts || [],
    has_app_download: state?.config?.webappConfig?.has_app_download,
    apiGWPrefix: state.config.apiGWPrefix
  }),
  null
)(BulkPickup);
